import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import React, { memo, useContext, useMemo } from "react";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import useBackwardsCompatible from '../../../hooks/useBackwardsCompatible';
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard, DashboardQuerySource } from "../../../types/files";
import { ReactComponent as PersonIcon } from '../../../assets/icons/person.svg';

const useStyles = makeStyles((theme) => ({
    card: {
        width: 400,
        height: 'auto',
        overflow: 'hidden',
        padding: 16,
        borderRadius: 8,
        backgroundColor: '#eff6ff',
        border: `1px solid #dbeafe`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
    },
    dealSource: {
        width: '100%',
        height: 30,
        background: theme.colors.blue['100'],
        paddingLeft: 16,
    },
    container: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        ...scrollbarStyle,
    },
    masonryElement: {
        width: '100%',
        height: 'fit-content',
        padding: '12px 24px 8px 16px',
        transition: 'all 300ms ease',
    },
    link: {
        display: 'inline-block',
        fontFamily: 'Inter !important',
        fontSize: '1rem',
        fontWeight: 'bold',
        textDecoration: 'none',
        color: theme.palette.primary.main,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineClamp: 1,
    },
    email: {
        display: 'inline-block',
        fontFamily: 'Inter !important',
        fontSize: '0.95rem',
        textDecoration: 'none',
        color: theme.colors.neutral['500'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineClamp: 1,
    },
    source: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Lato',
        fontSize: '1.2rem',
        fontWeight: '600',
        color: '#1d4ed8',
    },
    sublabel: {
        fontFamily: 'Lato',
        fontWeight: '400',
        fontSize: '0.9rem',
        color: '#64748b',
    },
}));

const DealSourceContentCard: React.FC<{ dashboard: Dashboard, onClose: () => void }> = ({ dashboard, onClose }) => {
    const classes = useStyles();
    const { mappedOverviewQueries } = useContext(DashboardsContext);
    const { dashboardSource } = useBackwardsCompatible();

    const keyPeople = useMemo(() =>
        mappedOverviewQueries.get(`${dashboard.id}:Key People`)
    , [dashboard.id, mappedOverviewQueries]);

    const source = useMemo(() => {
        const dealSource = dashboardSource(dashboard);

        return (dealSource?.name !== DashboardQuerySource.LINKEDIN) ? dealSource : undefined;
    }, [dashboard, dashboardSource]);

    if (!Array.isArray(keyPeople) || keyPeople.length === 0)
        return (<></>);

    return (<>
        <Stack direction="column" className={classes.card} spacing={1} justifyContent="stretch">
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                    <Box px={1.5} py={1.15} sx={{ background: '#e2e8f0', borderRadius: 2 }}>
                        <PersonIcon style={{ width: 32, height: 32, stroke: '#64748b' }} />
                    </Box>
                    <Stack direction="column" alignItems="flex-start" justifyContent="center">
                        <Stack className={classes.container}>
                            {!!source?.name && (
                                <Typography className={classes.source}>
                                    {source?.name}
                                </Typography>
                            )}
                            {!!source?.email && (
                                <Typography className={classNames(classes.source, classes.sublabel)}>
                                    {source?.email}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="flex-end">
                    <IconButton onClick={() => onClose()} disableRipple>
                        <CloseIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                </Stack>
            </Stack>
        </Stack>
    </>);
};

export default memo(DealSourceContentCard);
